// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ContentCopyRounded,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  Warehouse as WarehouseIcon,
} from '@/constants/hardfin-standard-icons';
import { PurchaseOrderSyncSignal } from '@/purchase-order/components/purchase-order-sync-signal';
import {
  POStatus,
  isCancelled,
  isFullyReceived,
} from '@/purchase-order/constants/status';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';

interface HeaderActionsProps {
  status: POStatus;
  isThirdPartyReceive: boolean;
  isDestinationThirdParty: boolean;
  hasZeroQuantityItem: boolean;
  purchaseOrderKey: string;
  purchaseOrderNumber: string;
  lastSyncedAt: DateTime | null;
  lastThirdPartyUpdateAt: DateTime | null;
  hasSyncError: boolean;
  syncErrorMessage: string;
  onEdit: () => void;
  onCancel: () => void;
  onReceive: () => void;
  onSendTo3PL: () => void;
  onClone: () => void;
}

const ZERO_QUANTITY_TOOLTIP =
  'Edit the order and remove or set a positive quantity on every line item before continuing';

/**
 * Component that displays action buttons in the purchase order header.
 * Shows different buttons based on the current status.
 */
export function PurchaseOrderHeaderActions(props: HeaderActionsProps) {
  const {
    status,
    isThirdPartyReceive,
    isDestinationThirdParty,
    hasZeroQuantityItem,
    purchaseOrderKey,
    purchaseOrderNumber,
    lastSyncedAt,
    lastThirdPartyUpdateAt,
    hasSyncError,
    syncErrorMessage,
    onEdit,
    onCancel,
    onReceive,
    onSendTo3PL,
    onClone,
  } = props;

  // Determine button visibility and state based on status
  const showEdit = status === 'DRAFT' && !isThirdPartyReceive;

  // The 3PL sync signal shows for any 3PL-bound order regardless of status, so
  // ops can read sync timing and re-check a stuck order.
  const showSyncSignal = isThirdPartyReceive;
  const showSendTo3PL =
    status === 'DRAFT' && !isThirdPartyReceive && isDestinationThirdParty;
  const showReceive =
    !isThirdPartyReceive &&
    !isDestinationThirdParty &&
    !isCancelled(status) &&
    !isFullyReceived(status);
  // Only draft orders can be cancelled, and 3PL orders cannot be cancelled once sent
  const showCancel = status === 'DRAFT' && !isThirdPartyReceive;

  // Only block draft-advancing actions while still in DRAFT — once the order
  // has moved past DRAFT the user can no longer Edit, so blocking would leave
  // them with no path forward.
  const blockDraftAdvance = status === 'DRAFT' && hasZeroQuantityItem;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Edit button */}
      {showEdit && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EditIcon />}
          onClick={onEdit}
        >
          Edit
        </Button>
      )}

      {/* 3PL sync-status signal */}
      {showSyncSignal && (
        <PurchaseOrderSyncSignal
          purchaseOrderKey={purchaseOrderKey}
          purchaseOrderNumber={purchaseOrderNumber}
          status={status}
          lastSyncedAt={lastSyncedAt}
          lastThirdPartyUpdateAt={lastThirdPartyUpdateAt}
          hasSyncError={hasSyncError}
          syncErrorMessage={syncErrorMessage}
        />
      )}

      {/* Send to 3PL button - only for draft orders with 3PL destination */}
      {showSendTo3PL && (
        <Tooltip arrow title={blockDraftAdvance ? ZERO_QUANTITY_TOOLTIP : ''}>
          <span>
            <Button
              variant="contained"
              size="medium"
              startIcon={<WarehouseIcon />}
              onClick={onSendTo3PL}
              disabled={blockDraftAdvance}
            >
              Send to 3PL
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Receive button - only for non-3PL orders */}
      {showReceive && (
        <Tooltip arrow title={blockDraftAdvance ? ZERO_QUANTITY_TOOLTIP : ''}>
          <span>
            <Button
              variant="contained"
              size="medium"
              startIcon={<LocalShippingIcon />}
              onClick={onReceive}
              disabled={blockDraftAdvance}
            >
              Record shipment
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Cancel order button */}
      {showCancel && (
        <Button
          variant="contained"
          size="medium"
          color="error"
          onClick={onCancel}
        >
          Cancel order
        </Button>
      )}

      {/* Clone button */}
      <Tooltip arrow title="Clone this purchase order">
        <IconButton size="small" onClick={onClone}>
          <ContentCopyRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
